import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Link } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { Loader2, Zap } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { LanguageProvider, useLang } from "@/i18n/LanguageContext";
import { SEO } from "@/components/SEO";
import { api, trackClicSoumission } from "@/lib/api";
import { initPixels, trackPageView } from "@/lib/pixels";
import { pingPresence, startPresenceHeartbeat, getPresenceSid } from "@/lib/presence";
import { noterRechercheExterne } from "@/lib/recherches";
import AuthCallback from "@/components/AuthCallback";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MessengerBubble from "@/components/MessengerBubble";
import InlineEditMode from "@/components/InlineEditMode";
import ScrollTop from "@/components/ScrollTop";
import Home from "@/pages/Home";
import Catalogue from "@/pages/Catalogue";
import Animaux from "@/pages/Animaux";
import TropheesHockey from "@/pages/TropheesHockey";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import Vendeur from "@/pages/Vendeur";
import GuideVendeur from "@/pages/GuideVendeur";
import PlaqueSurMesure from "@/pages/PlaqueSurMesure";
import About from "@/pages/About";
import Faq from "@/pages/Faq";
import Forfaits from "@/pages/Forfaits";
import AvisMagique from "@/pages/AvisMagique";
import Contact from "@/pages/Contact";
import Rendezvous from "@/pages/Rendezvous";
import QuickQuote from "@/pages/QuickQuote";
import QuoteRespond from "@/pages/QuoteRespond";
import Epreuve from "@/pages/Epreuve";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Conditions";
import Returns from "@/pages/Returns";
import CategoryPage from "@/pages/CategoryPage";
import Reorder from "@/pages/Reorder";
import Realisations from "@/pages/Realisations";
import Catalogues from "@/pages/Catalogues";
import CatalogueViewer from "@/pages/CatalogueViewer";
import CatalogueInteractif from "@/pages/CatalogueInteractif";
import ConfirmEmail from "@/pages/ConfirmEmail";
import Unsubscribe from "@/pages/Unsubscribe";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Occasions from "@/pages/Occasions";
import OccasionPage from "@/pages/OccasionPage";
import Villes from "@/pages/Villes";
import VillePage from "@/pages/VillePage";
import LivraisonMainPropre from "@/pages/LivraisonMainPropre";
import LivraisonMontreal from "@/pages/LivraisonMontreal";
import Referral from "@/pages/Referral";
import PartnerBadge from "@/pages/PartnerBadge";
import SharedCart from "@/pages/SharedCart";
import ShopClosedBanner from "@/components/ShopClosedBanner";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";
import ForfaitBanner from "@/components/ForfaitBanner";
import { useShopSettings, isSectionOff } from "@/lib/shopSettings";
import MaintenanceGate from "@/components/MaintenanceGate";
import ThemeSaison from "@/components/ThemeSaison";
import PageGate from "@/components/PageGate";
import { installerVerrouDefilementPopups } from "@/lib/popupScrollLock";

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    initPixels();
    // F5 : revenir là où la page était (demande du propriétaire, 2026-10-08) ;
    // navigation par lien : en haut, comme avant. Le drapeau « mt-reload-path » est
    // posé au beforeunload — il distingue un VRAI rechargement d'une navigation SPA.
    const reloadPath = sessionStorage.getItem("mt-reload-path");
    const saved = Number(sessionStorage.getItem("mt-scroll:" + pathname) || 0);
    if (reloadPath === pathname && saved > 0) {
      // Le contenu arrive en async (données, images) — plusieurs tentatives pendant
      // le chargement, puisque la hauteur de la page grandit
      [150, 450, 1000, 2000, 3500].forEach((ms) =>
        setTimeout(() => {
          const l = window.__lenis;
          if (l && l.scrollTo) l.scrollTo(saved, { immediate: true, force: true });
          else window.scrollTo(0, saved);
        }, ms));
    } else {
      window.scrollTo(0, 0);
    }
    sessionStorage.removeItem("mt-reload-path");
    trackPageView();
    pingPresence(pathname);
  }, [pathname]);
  useEffect(() => {
    // Restauration manuelle : on désactive celle du navigateur pour éviter les
    // conflits avec Lenis et le chargement async
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    let t = null;
    const save = () => {
      if (t) return;
      t = setTimeout(() => {
        t = null;
        sessionStorage.setItem("mt-scroll:" + window.location.pathname, String(window.scrollY));
      }, 250);
    };
    const mark = () => sessionStorage.setItem("mt-reload-path", window.location.pathname);
    window.addEventListener("scroll", save, { passive: true });
    window.addEventListener("beforeunload", mark);
    return () => {
      window.removeEventListener("scroll", save);
      window.removeEventListener("beforeunload", mark);
      if (t) clearTimeout(t);
    };
  }, []);
  useEffect(() => {
    const id = startPresenceHeartbeat(() => window.location.pathname);
    return () => clearInterval(id);
  }, []);
  return null;
}

// Pages réservées aux clients inscrits : catalogues interactifs, boutique, caisse.
function RequireAuth({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div data-testid="auth-gate-loading" className="min-h-[60vh] flex items-center justify-center pt-24">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }
  if (user === false) {
    return <Navigate to="/connexion" state={{ next: location.pathname + location.search }} replace />;
  }
  return children;
}

// Anciennes pages .html de l'ancien site (indexées par Google) → nouvelles pages.
// Filet de sécurité React : les fichiers statiques dans public/ interceptent la plupart
// des requêtes, mais certains serveurs ne décodent pas les noms accentués.
const OLD_HTML_REDIRECTS = {
  "/catalogues.html": "/catalogues",
  "/contact.html": "/contact",
  "/nos_services.html": "/",
  "/prêt_à_lancer_votre_projet_.html": "/contact",
};

function LegacyRedirect() {
  const location = useLocation();
  let path = location.pathname;
  try { path = decodeURIComponent(path); } catch { /* chemin déjà décodé */ }
  const target = OLD_HTML_REDIRECTS[path];
  if (target) return <Navigate to={target} replace />;
  return <NotFound />;
}

// Bandeau « Saison des tournois » sur TOUTES les pages — sauf l'accueil (qui affiche
// le sien sous le bandeau promo) et l'admin. pt-20 : espace pour le menu fixe.
function ForfaitBannerGlobal() {
  const { pathname } = useLocation();
  const shopSettings = useShopSettings();
  // URLs anglaises /en/… : le préfixe est retiré pour la DÉCISION — sinon /en
  // (accueil anglais) afficherait ce bandeau EN PLUS de celui de la page d'accueil
  const p = pathname === "/en" ? "/" : pathname.startsWith("/en/") ? pathname.slice(3) : pathname;
  if (p === "/" || p.startsWith("/admin")) return null;
  if (isSectionOff(shopSettings, "bandeau_forfaits")) return null;
  return <div className="pt-20"><ForfaitBanner /></div>;
}

function NotFound() {
  const { lang } = useLang();
  return (
    <div data-testid="not-found-page" className="min-h-[70vh] flex flex-col items-center justify-center px-6 pt-24 pb-16 text-center">
      <SEO
        title={lang === "fr" ? "Page introuvable | Mr Trophée" : "Page not found | Mr Trophée"}
        description={lang === "fr" ? "Cette page n'existe pas." : "This page does not exist."}
        noindex
      />
      <p className="font-serif text-7xl text-gold mb-6">404</p>
      <h1 className="font-serif text-2xl mb-8">
        {lang === "fr" ? "Cette page n'existe pas ou a été déplacée." : "This page doesn't exist or has moved."}
      </h1>
      <Link
        to="/" data-testid="not-found-home"
        className="inline-flex items-center gap-3 bg-gold text-ink font-semibold px-8 py-4 rounded-full hover:bg-champagne transition-colors duration-300"
      >
        {lang === "fr" ? "Retour à l'accueil" : "Back to home"}
      </Link>
    </div>
  );
}


// Page « Accès refusé » — adresse IP bloquée par le propriétaire (bouton
// « Bloquer » de l'historique des visiteurs, 2026-10-16) : l'intercepteur api
// y redirige dès le premier 403 « Accès refusé » ; le backend refuse déjà
// toute requête /api de cette IP.
function Bloque() {
  return (
    <div data-testid="page-bloquee" className="min-h-[70vh] flex flex-col items-center justify-center px-6 pt-24 pb-16 text-center">
      <SEO title="Accès refusé | Mr Trophée" description="Accès refusé." noindex />
      <p className="font-serif text-2xl mb-4">Accès refusé</p>
      <p className="text-ivory/70 max-w-md text-sm">
        Cette adresse n'a pas accès au site. Une erreur&nbsp;? Écrivez-nous à info@trophee.ca.
      </p>
    </div>
  );
}


// Bouton doré flottant « Soumission en 30 s » — sur TOUTES LES PAGES sans exception,
// tout en bas à droite : SOUS la bulle de clavardage, jamais caché par un autre bouton.
// Exceptions : /forfaits (2026-10-17, la grande section CTA « Parlons-en » fait double
// emploi) ET les écrans de TRAVAIL /admin et /vendeur (2026-10-22, demande du
// propriétaire : la pastille recouvrait les fiches sur téléphone — lisibilité).
function QuickQuoteFab() {
  const { t } = useLang();
  const location = useLocation();
  const chemin = location.pathname.replace(/^\/en(?=\/)/, "");
  if (chemin === "/forfaits" || chemin === "/admin" || chemin === "/vendeur") return null;
  return (
    <Link to="/soumission-rapide" data-testid="quick-quote-fab"
      onClick={() => trackClicSoumission("pastille")}
      className="fixed bottom-6 right-4 sm:right-6 z-[9990] inline-flex items-center gap-1.5 bg-gold text-ink text-xs font-bold px-3 py-1.5 rounded-full shadow-lg shadow-gold/30 hover:bg-champagne hover:-translate-y-0.5 transition-all duration-300">
      <span className="absolute inset-0 rounded-full bg-gold/40 animate-ping [animation-duration:2.8s]" aria-hidden="true" />
      <Zap className="w-3 h-3 relative" /> <span className="relative">{t("quote.fab")}</span>
    </Link>
  );
}

function AppRouter() {
  const location = useLocation();
  const { setLang } = useLang();
  // Version ANGLAISE à URLs dédiées /en/… (demande du propriétaire, 2026-10-06 —
  // capter le marché anglophone : pages indexables + hreflang). Le préfixe impose
  // l'anglais puis est retiré pour le routage : la MÊME page rend, en anglais.
  const isEn = location.pathname === "/en" || location.pathname.startsWith("/en/");
  useEffect(() => { if (isEn) setLang("en"); }, [isEn]);  // eslint-disable-line react-hooks/exhaustive-deps
  // Détection du retour OAuth Google : traiter le session_id AVANT les routes normales
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const loc = isEn ? { ...location, pathname: location.pathname.slice(3) || "/" } : location;
  return (
    <Routes location={loc}>
      <Route path="/" element={<Home />} />
      <Route path="/boutique" element={<PageGate pageKey="boutique"><Catalogue /></PageGate>} />
      <Route path="/boutique/animaux" element={<PageGate pageKey="boutique"><Animaux /></PageGate>} />
      {/* Page SEO « Trophées de hockey » (demande du propriétaire, 2026-10-27 —
          sport le plus recherché, choisi selon le calendrier : saison oct. → avr.) */}
      <Route path="/trophees-hockey" element={<TropheesHockey />} />
      <Route path="/forfaits" element={<PageGate pageKey="forfaits"><Forfaits /></PageGate>} />
      <Route path="/avis/:token" element={<AvisMagique />} />
      <Route path="/produit/:id" element={<ProductDetail />} />
      <Route path="/recommande/:orderId" element={<RequireAuth><Reorder /></RequireAuth>} />
      <Route path="/panier" element={<Cart />} />
      {/* /commande : pas de RequireAuth — Checkout affiche lui-même l'écran
          « inscription obligatoire » (explication + création de compte d'abord) */}
      <Route path="/commande" element={<Checkout />} />
      <Route path="/paiement/succes" element={<PaymentSuccess />} />
      <Route path="/paiement/annule" element={<PaymentCancel />} />
      <Route path="/connexion" element={<Login />} />
      <Route path="/inscription" element={<Register />} />
      <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
      <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
      <Route path="/confirmer-courriel" element={<ConfirmEmail />} />
      <Route path="/desinscription" element={<Unsubscribe />} />
      <Route path="/compte" element={<Account />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/vendeur" element={<Vendeur />} />
      <Route path="/guide-vendeur" element={<PageGate pageKey="guide-vendeur"><GuideVendeur /></PageGate>} />
      <Route path="/plaquettes-sur-mesure" element={<PageGate pageKey="plaquettes"><PlaqueSurMesure /></PageGate>} />
      {/* Alias sous le nouveau nom « Gravure sur mesure » (demande du propriétaire,
          2026-10-16) — l'URL historique reste le canonique pour le référencement */}
      <Route path="/gravure-sur-mesure" element={<PageGate pageKey="plaquettes"><PlaqueSurMesure /></PageGate>} />
      <Route path="/a-propos" element={<PageGate pageKey="a-propos"><About /></PageGate>} />
      <Route path="/faq" element={<PageGate pageKey="faq"><Faq /></PageGate>} />
      <Route path="/confidentialite" element={<Privacy />} />
      <Route path="/conditions" element={<Terms />} />
      <Route path="/retours" element={<Returns />} />
      <Route path="/categorie/:slug" element={<PageGate pageKey="boutique"><CategoryPage /></PageGate>} />
      <Route path="/contact" element={<PageGate pageKey="contact"><Contact /></PageGate>} />
      <Route path="/rendez-vous" element={<Rendezvous />} />
      <Route path="/soumission-rapide" element={<PageGate pageKey="soumission"><QuickQuote /></PageGate>} />
      <Route path="/soumission/:token" element={<QuoteRespond />} />
      <Route path="/epreuve/:token" element={<Epreuve />} />
      <Route path="/realisations" element={<PageGate pageKey="realisations"><Realisations /></PageGate>} />
      <Route path="/catalogues" element={<PageGate pageKey="catalogues"><Catalogues /></PageGate>} />
      <Route path="/catalogue-viewer" element={<PageGate pageKey="catalogues"><CatalogueViewer /></PageGate>} />
      <Route path="/catalogue-interactif" element={<PageGate pageKey="boutique"><CatalogueInteractif /></PageGate>} />
      <Route path="/blogue" element={<PageGate pageKey="blogue"><Blog /></PageGate>} />
      <Route path="/blogue/:slug" element={<PageGate pageKey="blogue"><BlogPost /></PageGate>} />
      <Route path="/occasions" element={<PageGate pageKey="boutique"><Occasions /></PageGate>} />
      <Route path="/occasions/:slug" element={<PageGate pageKey="boutique"><OccasionPage /></PageGate>} />
      <Route path="/villes" element={<PageGate pageKey="boutique"><Villes /></PageGate>} />
      <Route path="/villes/:slug" element={<PageGate pageKey="boutique"><VillePage /></PageGate>} />
      <Route path="/livraison-main-propre" element={<PageGate pageKey="boutique"><LivraisonMainPropre /></PageGate>} />
      <Route path="/livraison-montreal" element={<LivraisonMontreal />} />
      <Route path="/parrainage" element={<PageGate pageKey="parrainage"><Referral /></PageGate>} />
      <Route path="/badge-partenaire" element={<PageGate pageKey="badge"><PartnerBadge /></PageGate>} />
      <Route path="/panier-partage/:token" element={<SharedCart />} />
      <Route path="/bloque" element={<Bloque />} />
      <Route path="/catalogue" element={<Navigate to="/boutique" replace />} />
      <Route path="*" element={<LegacyRedirect />} />
    </Routes>
  );
}

// Enregistre chaque page vue (anonyme — statistiques de visite de l'admin)
function PageTracker() {
  const { pathname } = useLocation();
  const { lang } = useLang();
  const firstRef = useRef(document.referrer || "");
  const lastPath = useRef("");
  const geoRef = useRef(null);  // null = pas encore demandé ; false = refusé/indispo
  useEffect(() => {
    // Position GPS CONSENTIE (demande du propriétaire 2026-10-10) : le navigateur
    // demande la permission UNE fois par session ; si le visiteur accepte, sa
    // position accompagne ses pages vues dans l'historique des visiteurs (admin)
    if (geoRef.current === null) {
      geoRef.current = false;
      try {
        const cached = sessionStorage.getItem("mt_geo");
        if (cached) {
          geoRef.current = JSON.parse(cached);
        } else if (!sessionStorage.getItem("mt_geo_refuse") && navigator.geolocation) {
          navigator.geolocation.getCurrentPosition((pos) => {
            const g = { lat: +pos.coords.latitude.toFixed(5), lng: +pos.coords.longitude.toFixed(5) };
            geoRef.current = g;
            sessionStorage.setItem("mt_geo", JSON.stringify(g));
            // La permission arrive souvent APRÈS la 1re page vue : on envoie un
            // événement géolocalisé dès le consentement
            api.post("/analytics/event", { path: window.location.pathname, referrer: "", lang, sid: getPresenceSid(), geo: g }).catch(() => {});
          }, () => { sessionStorage.setItem("mt_geo_refuse", "1"); },
          { timeout: 8000, maximumAge: 1800000 });
        }
      } catch { /* stockage de session indisponible */ }
    }
    if (pathname === lastPath.current) return;
    const ref = firstRef.current;  // source externe envoyée à la 1re page vue seulement
    firstRef.current = "";
    lastPath.current = pathname;
    const payload = { path: pathname, referrer: ref, lang, sid: getPresenceSid() };
    if (geoRef.current) payload.geo = geoRef.current;
    api.post("/analytics/event", payload).catch(() => {});
    // Recherche venue d'AILLEURS (demande du propriétaire, 2026-10-27) : terme
    // du moteur d'origine ou utm_term de campagne → noté une fois par session,
    // les produits associés (ex. soccer) s'affichent d'emblée à l'accueil/boutique
    noterRechercheExterne();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
  return null;
}

// Redirige /page/ → /page : une seule URL par page, sinon Google voit des doublons
// (« Page en double sans URL canonique » dans Search Console)
function TrailingSlashRedirect() {
  const { pathname, search } = useLocation();
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return <Navigate to={pathname.replace(/\/+$/, "") + search} replace />;
  }
  return null;
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    window.__lenis = lenis;  // les popups le stoppent/redémarrent (lib/scrollLock)
    installerVerrouDefilementPopups();  // verrou global des popups (arrière-plan ne défile plus)
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
      delete window.__lenis;
    };
  }, []);

  return (
    <div className="grain min-h-screen bg-ink text-ivory">
      <HelmetProvider>
      <BrowserRouter>
        <LanguageProvider>
        <AuthProvider>
          <CartProvider>
            <ScrollManager />
            <PageTracker />
            <ThemeSaison />
            <TrailingSlashRedirect />
            <Navbar />
            <ShopClosedBanner />
            <VerifyEmailBanner />
            <ForfaitBannerGlobal />
            {/* pb généreux (demande du propriétaire) : sur mobile, les boutons
                flottants (Soumission 30 s, clavardage, haut de page) ne doivent
                JAMAIS chevaucher le texte — le contenu peut défiler au-delà */}
            <main className="pb-32 sm:pb-28">
              <MaintenanceGate>
                <AppRouter />
              </MaintenanceGate>
            </main>
            <Footer />
            <QuickQuoteFab />
            <MessengerBubble />
            <InlineEditMode />
            <ScrollTop />
            <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#111111", border: "1px solid rgba(212,175,55,0.25)", color: "#F5F5F0" } }} />
          </CartProvider>
        </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
